import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Filters,
  RangeSlider,
  ChoiceList,
  InlineStack,
} from '@shopify/polaris';
import type { BulkFilterCriteria, BulkJobType } from '../../types';

/**
 * T115 [US4]: BulkFilterBar — filter products by score, issue type,
 * collection, status, and date range (FR-026).
 *
 * @see contracts/bulk.md, FR-026
 */

interface BulkFilterBarProps {
  onStartJob: (jobType: BulkJobType, filter: BulkFilterCriteria) => void;
  isLoading?: boolean;
}

export function BulkFilterBar({ onStartJob, isLoading }: BulkFilterBarProps) {
  const [scoreRange, setScoreRange] = useState<[number, number]>([0, 100]);
  const [issueType, setIssueType] = useState<string[]>([]);
  const [jobType, setJobType] = useState<BulkJobType>('FIX_PREVIEW');

  const buildFilter = useCallback((): BulkFilterCriteria => {
    const filter: BulkFilterCriteria = {};
    if (scoreRange[0] > 0 || scoreRange[1] < 100) {
      filter.scoreMin = scoreRange[0];
      filter.scoreMax = scoreRange[1];
    }
    if (issueType.length > 0) {
      filter.issueType = issueType[0];
    }
    return filter;
  }, [scoreRange, issueType]);

  const handleStartJob = useCallback(() => {
    onStartJob(jobType, buildFilter());
  }, [jobType, buildFilter, onStartJob]);

  const handleScoreChange = useCallback(
    (value: [number, number]) => setScoreRange(value),
    [],
  );

  const handleClearAll = useCallback(() => {
    setScoreRange([0, 100]);
    setIssueType([]);
  }, []);

  const filters = [
    {
      key: 'scoreRange',
      label: 'Quality Score',
      filter: (
        <RangeSlider
          label="Score range"
          value={scoreRange}
          min={0}
          max={100}
          step={5}
          output
          onChange={handleScoreChange}
        />
      ),
      shortcut: true,
    },
    {
      key: 'issueType',
      label: 'Issue Type',
      filter: (
        <ChoiceList
          title="Issue type"
          titleHidden
          choices={[
            { label: 'Missing title', value: 'missing_title' },
            { label: 'Short description', value: 'short_description' },
            { label: 'Missing images', value: 'missing_images' },
            { label: 'Missing identifiers', value: 'missing_identifiers' },
            { label: 'Boilerplate content', value: 'boilerplate' },
          ]}
          selected={issueType}
          onChange={setIssueType}
          allowMultiple
        />
      ),
      shortcut: true,
    },
  ];

  return (
    <Box>
      <Filters
        queryValue=""
        queryPlaceholder="Filter products..."
        filters={filters}
        onQueryChange={() => {}}
        onQueryClear={() => {}}
        onClearAll={handleClearAll}
      />
      <Box paddingBlockStart="400">
        <InlineStack gap="300" align="start">
          <ChoiceList
            title="Job type"
            titleHidden
            choices={[
              { label: 'Scan', value: 'SCAN' },
              { label: 'Fix Preview', value: 'FIX_PREVIEW' },
              { label: 'Apply Fixes', value: 'APPLY' },
              { label: 'Export', value: 'EXPORT' },
            ]}
            selected={[jobType]}
            onChange={(v) => setJobType(v[0] as BulkJobType)}
          />
          <Button
            variant="primary"
            onClick={handleStartJob}
            loading={isLoading}
          >
            Start Bulk Job
          </Button>
        </InlineStack>
      </Box>
    </Box>
  );
}
